import { Injectable } from '@angular/core';
import {
  ListAction,
  ListErrorHandlerService,
} from './list-error-handler.service';

export type ProcessErrorCodeFn = (
  itemName?: string | null | Record<string, unknown>,
  action?: ListAction | null | Record<string, unknown>,
  error?: unknown,
  errorCodes?: Array<string | number>,
) => string;

/**
 * Bridges the legacy variadic `processErrorCode` callable signature to
 * `ListErrorHandlerService`.
 */
@Injectable({
  providedIn: 'root',
})
export class ProcessErrorCodeAdapterService {
  constructor(private listErrorHandler: ListErrorHandlerService) {}

  createCallable(): ProcessErrorCodeFn {
    return (
      itemName?: string | null | Record<string, unknown>,
      action?: ListAction | null | Record<string, unknown>,
      error?: unknown,
      errorCodes?: Array<string | number>,
    ) => {
      const normalized = this.normalizeArgs(
        itemName,
        action,
        error,
        errorCodes,
      );
      return this.listErrorHandler.processErrorCode(
        normalized.itemName,
        normalized.action,
        normalized.error,
        normalized.errorCodes,
      );
    };
  }

  private normalizeArgs(
    itemName?: string | null | Record<string, unknown>,
    action?: ListAction | null | Record<string, unknown>,
    error?: unknown,
    errorCodes?: Array<string | number>,
  ): {
    itemName?: string | null;
    action?: ListAction | null;
    error?: unknown;
    errorCodes?: Array<string | number>;
  } {
    if (
      error === undefined &&
      action === undefined &&
      itemName !== undefined &&
      itemName !== null &&
      typeof itemName === 'object'
    ) {
      return {
        itemName: undefined,
        action: undefined,
        error: itemName,
        errorCodes,
      };
    }

    const resolvedAction = this.asListAction(action);
    const resolvedItemName: string | null | undefined =
      typeof itemName === 'string'
        ? itemName
        : itemName === null
          ? null
          : undefined;

    return {
      itemName: resolvedItemName,
      action: resolvedAction,
      error: error ?? itemName,
      errorCodes,
    };
  }

  private asListAction(
    action?: ListAction | null | Record<string, unknown>,
  ): ListAction | null | undefined {
    if (!action || typeof action === 'object') {
      return undefined;
    }

    return action;
  }
}
